/**
 * Cell Events Module
 *
 * Handles keyboard and mouse events for puzzle cells.
 * Migrated from event handlers in olaszmodra.js.
 */

import type { Cell } from '../core/Cell';
import type { Grid } from '../core/Grid';
import type { HistoryManager } from '../core/HistoryManager';
import { KeyCode } from '../types/events.types';
import type { CellEventContext } from '../types/events.types';

/**
 * Configuration for CellEvents
 */
export interface CellEventsConfig {
    /** Function to get the puzzle grid for a given cell ID */
    getGrid: (cellId: number) => Grid;
    /** Function to get a cell by ID */
    getCell: (cellId: number) => Cell;
    /** Function to get the next cell in sync editing mode */
    getNextSyncCell?: (cellId: number) => Cell;
    /** History manager for undo/redo */
    history: HistoryManager;
    /** Event context with state accessors */
    context: CellEventContext;
    /** Callback when cell selection changes */
    onSelectionChange?: (cellId: number, isHorizontal: boolean) => void;
    /** Callback when menu should be updated */
    onMenuUpdate?: () => void;
    /** Callback when history bar should be updated */
    onHistoryUpdate?: () => void;
    /** Callback when clue bar should be updated */
    onClueUpdate?: () => void;
    /** Callback to show save/close indicators */
    onStateChange?: () => void;
    /** Callback when direction should be toggled (Space key) */
    onToggleDirection?: (element: HTMLElement) => void;
    /** Callback for cell operations (hotkeys) */
    onSetColored?: (element: HTMLElement) => void;
    onSetBridge?: (element: HTMLElement) => void;
    onSetImage?: (element: HTMLElement) => void;
    onSetBlack?: (element: HTMLElement) => void;
    onSetVerticalLine?: (element: HTMLElement) => void;
    onSetHorizontalLine?: (element: HTMLElement) => void;
    onSetKnown?: (element: HTMLElement) => void;
    onToggleSync?: (element: HTMLElement) => void;
    onSave?: () => void;
}

/**
 * Extract cell ID from DOM element
 */
export function domToCellId(element: HTMLElement): number {
    const id = element.getAttribute('id');
    if (!id) return 0;
    return parseInt(id.replace('mezo-', ''), 10);
}

/**
 * Get DOM element for a cell ID
 */
export function cellIdToDom(cellId: number): HTMLElement | null {
    return document.getElementById(`mezo-${cellId}`);
}

/**
 * Cell Events Handler
 *
 * Manages all keyboard and mouse events for puzzle cells.
 */
export class CellEvents {
    private readonly _config: CellEventsConfig;

    constructor(config: CellEventsConfig) {
        this._config = config;
    }

    // ============================================================================
    // Event Binding
    // ============================================================================

    /**
     * Attach events to all cells in a container
     */
    attachToCells(container: HTMLElement): void {
        const cells = container.querySelectorAll('[id^="mezo-"]');
        cells.forEach((cell) => {
            if (cell instanceof HTMLElement) {
                this.attachToCell(cell);
            }
        });
    }

    /**
     * Attach events to a single cell element
     */
    attachToCell(element: HTMLElement): void {
        element.contentEditable = 'true';

        element.addEventListener('mousedown', this.handleMouseDown);
        element.addEventListener('dblclick', this.handleDoubleClick);
        element.addEventListener('keypress', this.handleKeyPress);
        element.addEventListener('keydown', this.handleKeyDown);
        element.addEventListener('keyup', this.handleKeyUp);
    }

    /**
     * Detach events from a cell element
     */
    detachFromCell(element: HTMLElement): void {
        element.removeEventListener('mousedown', this.handleMouseDown);
        element.removeEventListener('dblclick', this.handleDoubleClick);
        element.removeEventListener('keypress', this.handleKeyPress);
        element.removeEventListener('keydown', this.handleKeyDown);
        element.removeEventListener('keyup', this.handleKeyUp);
    }

    // ============================================================================
    // Mouse Handlers
    // ============================================================================

    /**
     * Handle mouse down on cell - selects the cell
     */
    handleMouseDown = (e: Event): void => {
        const event = e as MouseEvent;
        const target = event.target as HTMLElement;
        this.switchToCell(target);
    };

    /**
     * Handle double click - toggle lines based on click position
     */
    handleDoubleClick = (e: Event): void => {
        const event = e as MouseEvent;
        const target = event.target as HTMLElement;

        const container = document.getElementById('rejtvenyKomponens');
        if (!container) return;

        const x = event.pageX - container.offsetLeft;
        const y = event.pageY - container.offsetTop - 84;

        // Check if clicked near right edge (horizontal line)
        if (
            target.offsetLeft + target.offsetWidth - 10 < x &&
            x < target.offsetLeft + target.offsetWidth
        ) {
            this._config.onSetHorizontalLine?.(target);
        }
        // Check if clicked near bottom edge (vertical line)
        else if (
            target.offsetTop + target.offsetHeight - 10 < y &&
            y < target.offsetTop + target.offsetHeight
        ) {
            this._config.onSetVerticalLine?.(target);
        }
    };

    // ============================================================================
    // Keyboard Handlers
    // ============================================================================

    /**
     * Handle key press - character input
     */
    handleKeyPress = (e: Event): void => {
        const event = e as KeyboardEvent;
        event.preventDefault();

        if (event.ctrlKey) return;

        const target = event.target as HTMLElement;
        const charCode = event.charCode || event.which;
        const char = String.fromCharCode(charCode);

        const cellId = domToCellId(target);
        const cell = this._config.getCell(cellId);
        const pattern = this._config.context.getValidCharPattern();
        const regex = new RegExp(pattern, 'g');

        if (!regex.test(char)) return;
        if (cell.isKnown) return;

        const isBuilderMode = this._config.context.getIsBuilderMode();
        if (!isBuilderMode && cell.isImage) return;

        // Handle sync editing - clear black/image in synced cell
        if (
            this._config.context.getSyncEditing() &&
            this._config.getNextSyncCell
        ) {
            const syncCell = this._config.getNextSyncCell(cellId);
            const syncElement = cellIdToDom(syncCell.id);
            if (syncElement && syncCell.isBlack) {
                this._config.onSetBlack?.(syncElement);
            } else if (syncElement && syncCell.isImage) {
                this._config.onSetImage?.(syncElement);
            }
        }

        // Clear special states if needed
        if (cell.isImage || cell.isBlack || cell.isEmpty) {
            this.clearCell(target);
        }

        // Set the character
        const upperChar = char.toUpperCase();
        target.innerHTML = upperChar;
        cell.setValue(upperChar);

        this.switchToCell(target, true);

        // Move to next cell
        const grid = this._config.getGrid(cellId);
        const isHorizontal = this._config.context.getIsHorizontal();
        const nextId = isHorizontal
            ? grid.nextCell(cellId)
            : grid.belowCell(cellId);

        const nextElement = cellIdToDom(nextId);
        if (nextElement) {
            this.switchToCell(nextElement);
        }

        // Save to history
        this._config.history.save(`Beírás ${upperChar}`);
        this._config.onHistoryUpdate?.();
        this._config.onMenuUpdate?.();
    };

    /**
     * Handle key down - keyboard shortcuts
     */
    handleKeyDown = (e: Event): void => {
        const event = e as KeyboardEvent;
        const target = event.target as HTMLElement;

        // Ctrl+S - Save
        if (event.ctrlKey && event.keyCode === KeyCode.S) {
            event.preventDefault();
            this._config.onSave?.();
            return;
        }

        // Ctrl+Z - Undo
        if (event.ctrlKey && event.keyCode === KeyCode.Z) {
            event.preventDefault();
            this._config.history.undo();
            this._config.onHistoryUpdate?.();
            return;
        }

        // Ctrl+Y - Redo
        if (event.ctrlKey && event.keyCode === KeyCode.Y) {
            event.preventDefault();
            this._config.history.redo();
            this._config.onHistoryUpdate?.();
            return;
        }

        // Ctrl+M - Toggle colored
        if (event.ctrlKey && event.keyCode === KeyCode.M) {
            event.preventDefault();
            this._config.onSetColored?.(target);
            return;
        }

        // Ctrl+H - Toggle bridge
        if (event.ctrlKey && event.keyCode === KeyCode.H) {
            event.preventDefault();
            this._config.onSetBridge?.(target);
            return;
        }

        // Ctrl+A - Toggle image
        if (event.ctrlKey && event.keyCode === KeyCode.A) {
            event.preventDefault();
            this._config.onSetImage?.(target);
            return;
        }

        // Ctrl+N - Toggle black
        if (event.ctrlKey && event.keyCode === KeyCode.N) {
            event.preventDefault();
            this._config.onSetBlack?.(target);
            return;
        }

        // Ctrl+V - Toggle vertical line
        if (event.ctrlKey && event.keyCode === KeyCode.V) {
            event.preventDefault();
            this._config.onSetVerticalLine?.(target);
            return;
        }

        // Ctrl+F - Toggle horizontal line
        if (event.ctrlKey && event.keyCode === KeyCode.F) {
            event.preventDefault();
            this._config.onSetHorizontalLine?.(target);
            return;
        }

        // Ctrl+E - Sync editing
        if (event.ctrlKey && event.keyCode === KeyCode.E) {
            event.preventDefault();
            this._config.onToggleSync?.(target);
            return;
        }

        // Ctrl+I - Toggle known
        if (event.ctrlKey && event.keyCode === KeyCode.I) {
            event.preventDefault();
            this._config.onSetKnown?.(target);
            return;
        }
    };

    /**
     * Handle key up - navigation and special keys
     */
    handleKeyUp = (e: Event): void => {
        const event = e as KeyboardEvent;
        const target = event.target as HTMLElement;
        const cellId = domToCellId(target);
        const grid = this._config.getGrid(cellId);

        switch (event.keyCode) {
            case KeyCode.LEFT:
                this.navigateTo(grid.previousCell(cellId));
                break;

            case KeyCode.UP:
                this.navigateTo(grid.aboveCell(cellId));
                break;

            case KeyCode.RIGHT:
                this.navigateTo(grid.nextCell(cellId));
                break;

            case KeyCode.DOWN:
                this.navigateTo(grid.belowCell(cellId));
                break;

            case KeyCode.TAB:
                // Tab to next clue cell (would need puzzle reference)
                event.preventDefault();
                break;

            case KeyCode.SPACE:
                this._config.onToggleDirection?.(target);
                break;

            case KeyCode.ENTER:
                this.handleEnterKey(target);
                break;

            case KeyCode.BACKSPACE:
                this.handleBackspace(target, cellId, grid);
                break;

            case KeyCode.DELETE:
                this.handleDelete(target);
                break;

            case KeyCode.INSERT:
                this.focusClueInput();
                break;
        }
    };

    // ============================================================================
    // Navigation
    // ============================================================================

    /**
     * Navigate to a cell by ID
     */
    navigateTo(cellId: number): void {
        const element = cellIdToDom(cellId);
        if (element) {
            this.switchToCell(element);
        }
    }

    /**
     * Switch to a cell element and update selection
     */
    switchToCell(element: HTMLElement, updateClues = false): void {
        const cellId = domToCellId(element);
        const cell = this._config.getCell(cellId);
        const isBuilderMode = this._config.context.getIsBuilderMode();

        // Don't select image cells in solving mode
        if (!isBuilderMode && cell.isImage) return;

        this.selectCell(element);

        // Update active cell indicator
        const activeIndicator = document.getElementById('aktivmezo');
        if (activeIndicator) {
            activeIndicator.style.left = `${element.offsetLeft}px`;
            activeIndicator.style.top = `${element.offsetTop}px`;
            activeIndicator.style.display = 'block';
        }

        // Update sync editing indicator
        if (
            this._config.context.getSyncEditing() &&
            this._config.getNextSyncCell
        ) {
            const syncCell = this._config.getNextSyncCell(cellId);
            const syncElement = cellIdToDom(syncCell.id);
            const syncIndicator = document.getElementById('aktivszinkronmezo');

            if (syncIndicator && syncElement) {
                syncIndicator.style.left = `${syncElement.offsetLeft}px`;
                syncIndicator.style.top = `${syncElement.offsetTop}px`;
                syncIndicator.style.display = 'block';
            }
        }

        // Update selection indicators
        this.updateSelectionIndicators();

        this._config.onSelectionChange?.(
            cellId,
            this._config.context.getIsHorizontal(),
        );
        if (updateClues) {
            this._config.onClueUpdate?.();
        }

        // Update menu to show current cell state
        this._config.onMenuUpdate?.();
    }

    /**
     * Select a cell and update state
     */
    private selectCell(element: HTMLElement): void {
        const cellId = domToCellId(element);
        const grid = this._config.getGrid(cellId);
        const isHorizontal = this._config.context.getIsHorizontal();

        // Clear previous highlighting
        this.clearHighlighting();

        // Update state
        this._config.context.setCurrentElement(element);
        this._config.context.setCurrentCellId(cellId);

        // Calculate new selections
        const selection = isHorizontal
            ? grid.selectHorizontalCells(cellId)
            : grid.selectVerticalCells(cellId);

        const inverseSelection = isHorizontal
            ? grid.selectVerticalCells(cellId)
            : grid.selectHorizontalCells(cellId);

        this._config.context.setCurrentSelection(selection);
        this._config.context.setInverseSelection(inverseSelection);

        // Focus the element
        element.focus();
    }

    /**
     * Update selection indicator dimensions
     */
    private updateSelectionIndicators(): void {
        const selection = this._config.context.getCurrentSelection();
        const currentId = this._config.context.getCurrentCellId();
        const isHorizontal = this._config.context.getIsHorizontal();

        // Include current cell in selection for indicator
        const fullSelection = [...selection, currentId].sort((a, b) => a - b);
        if (fullSelection.length === 0) return;

        const firstElement = cellIdToDom(fullSelection[0]);
        const indicator = document.getElementById('aktivtomb');

        if (!indicator || !firstElement) return;

        indicator.style.left = `${firstElement.offsetLeft}px`;
        indicator.style.top = `${firstElement.offsetTop}px`;

        const cellSize = 27;
        const gap = 1;
        const length =
            fullSelection.length * cellSize + (fullSelection.length - 1) * gap;

        if (isHorizontal) {
            indicator.style.width = `${length}px`;
            indicator.style.height = `${cellSize}px`;
        } else {
            indicator.style.width = `${cellSize}px`;
            indicator.style.height = `${length}px`;
        }

        indicator.style.display = 'block';
    }

    /**
     * Clear highlighting from previously selected cells
     */
    private clearHighlighting(): void {
        const previousSelection = this._config.context.getCurrentSelection();
        const previousInverse = this._config.context.getInverseSelection();

        [...previousSelection, ...previousInverse].forEach((id) => {
            const element = cellIdToDom(id);
            if (element) {
                element.style.opacity = '';
                if (element.style.backgroundColor === 'rgb(240, 240, 240)') {
                    element.style.backgroundColor = '';
                }
                element.style.color = '';
            }
        });
    }

    // ============================================================================
    // Special Key Handlers
    // ============================================================================

    /**
     * Handle Enter key - toggle highlight mode
     */
    private handleEnterKey(element: HTMLElement): void {
        const cellHighlight = this._config.context.getCellHighlight();
        const clueMarking = this._config.context.getClueMarking();

        const activeIndicator = document.getElementById('aktivmezo');
        const selectionIndicator = document.getElementById('aktivtomb');
        const syncIndicator = document.getElementById('aktivszinkronmezo');

        if (cellHighlight) {
            // Hide indicators
            if (activeIndicator) activeIndicator.style.display = 'none';
            if (selectionIndicator) selectionIndicator.style.display = 'none';
            if (syncIndicator) syncIndicator.style.display = 'none';

            this.resetCell(element, !clueMarking);
        } else {
            this.switchToCell(element);
        }

        this._config.context.setCellHighlight(!cellHighlight);
    }

    /**
     * Handle Backspace key - delete character and move back
     */
    private handleBackspace(
        element: HTMLElement,
        cellId: number,
        grid: Grid,
    ): void {
        this.clearCellValue(element);
        this.switchToCell(element, true);

        const isHorizontal = this._config.context.getIsHorizontal();
        const prevId = isHorizontal
            ? grid.previousCell(cellId)
            : grid.aboveCell(cellId);

        this.navigateTo(prevId);

        this._config.history.save('Karakter visszatörlés');
        this._config.onHistoryUpdate?.();
    }

    /**
     * Handle Delete key - clear cell completely
     */
    private handleDelete(element: HTMLElement): void {
        this.clearCell(element);
        this.switchToCell(element, true);

        this._config.history.save('Mező törlés');
        this._config.onHistoryUpdate?.();
    }

    /**
     * Reset cell styling
     */
    private resetCell(element: HTMLElement, clearAll: boolean): void {
        if (clearAll) {
            // Clear all cells
            const container = document.getElementById('rejtveny');
            if (container) {
                const cells = container.querySelectorAll('[id^="mezo-"]');
                cells.forEach((cell) => {
                    if (cell instanceof HTMLElement) {
                        cell.style.opacity = '';
                        if (
                            cell.style.backgroundColor === 'rgb(240, 240, 240)'
                        ) {
                            cell.style.backgroundColor = '';
                        }
                        cell.style.backgroundImage = '';
                        cell.style.color = '';
                    }
                });
            }
        } else {
            this.clearHighlighting();
        }

        element.style.opacity = '';
        element.style.color = '';
    }

    // ============================================================================
    // Cell Modifications
    // ============================================================================

    /**
     * Clear cell value only
     */
    clearCellValue(element: HTMLElement): void {
        const cellId = domToCellId(element);
        const cell = this._config.getCell(cellId);

        element.innerHTML = '';
        element.className = element.className.replace(
            /(fekete|ábra|üres|színes|híd)/g,
            '',
        );

        cell.clearValue();
        this._config.onMenuUpdate?.();
        element.focus();
    }

    /**
     * Clear entire cell (value and attributes)
     */
    clearCell(element: HTMLElement): void {
        const cellId = domToCellId(element);
        const cell = this._config.getCell(cellId);

        element.innerHTML = '';
        element.className = element.className
            .replace(/(fekete|ábra|üres|színes|híd)/g, '')
            .replace(/(vvonal|fvonal)/g, '');

        cell.clearCell();
        this._config.onMenuUpdate?.();
        element.focus();
    }

    // ============================================================================
    // Helper Methods
    // ============================================================================

    /**
     * Focus the clue input textarea
     */
    private focusClueInput(): void {
        const textarea = document.getElementById('textareaMeghatarozas');
        if (textarea) {
            textarea.focus();
        }
    }
}
